import React, { useState, useRef } from 'react';
import SelectFieldInline from '../../../../../../component-controller-mod/component-serv/input-fs-cp/select-field-op/ts/selectFieldInline';
import { ContactSection, ContactCardSection, ContactCard } from './types';
import { LabelRow, SplitRow, DraftTextField, DraftTextArea } from './primitives';

interface CompanyContactEditorProps {
  data:     ContactSection;
  onChange: (v: ContactSection) => void;
}

const CompanyContactEditor: React.FC<CompanyContactEditorProps> = ({ data, onChange }) => {
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [cardEdits,   setCardEdits]   = useState<Record<number, Partial<ContactCard>>>({});

  const cs   = data.content[0]?.cardSection;
  const orig = useRef(cs);

  const setCs = (p: Partial<ContactCardSection>) => onChange({
    ...data,
    content: [{ ...data.content[0], cardSection: { ...cs, ...p } }],
  });

  const setCardField = (field: keyof ContactCard, v: string) => {
    const updatedEdits = {
      ...cardEdits,
      [selectedIdx]: { ...(cardEdits[selectedIdx] ?? {}), [field]: v },
    };
    setCardEdits(updatedEdits);
    const updatedList = cs.list.map((item, i) => ({ ...item, ...(updatedEdits[i] ?? {}) }));
    setCs({ list: updatedList });
  };

  if (!cs) return null;

  const rawCard    = cs.list[selectedIdx];
  const editedCard = rawCard ? { ...rawCard, ...(cardEdits[selectedIdx] ?? {}) } : undefined;
  const origCard   = orig.current?.list[selectedIdx];
  const cardOptions = orig.current?.list.map((c, i) => ({
    value: String(i),
    label: `Card${i + 1}/ ${c.title}`,
  })) ?? [];

  return (
    <>
      <LabelRow label="Main content title" required />
      <SplitRow currentText={`[ ${orig.current?.label ?? ''} ]`}>
        <DraftTextField placeholder="Text title" onChange={v => setCs({ label: v })} />
      </SplitRow>

      <LabelRow label="Main title" />
      <SplitRow currentText={orig.current?.title ?? ''}>
        <DraftTextField placeholder="Text title" onChange={v => setCs({ title: v })} />
      </SplitRow>

      <LabelRow label="Main description" />
      <SplitRow currentText={orig.current?.description ?? ''} multiline>
        <DraftTextArea placeholder="Title description" rows={3}
          onChange={v => setCs({ description: v })} />
      </SplitRow>

      <div className="scl--cms-content-edit-company-card-row">
        <span className="scl--cms-content-edit-company-label">Card / Contact us</span>
        <div className="scl--cms-content-edit-company-card-row-right">
          <SelectFieldInline
            className="scl--cms-content-edit-company-card-select"
            value={String(selectedIdx)}
            options={cardOptions}
            onChange={(v: string) => setSelectedIdx(Number(v))}
          />
        </div>
      </div>
      <div className="scl--cms-content-edit-company-divider" />

      {editedCard && (
        <>
          <LabelRow label="Card title" />
          <SplitRow currentText={origCard?.title ?? ''}>
            <DraftTextField
              key={`contact-title-${selectedIdx}`}
              initialValue={cardEdits[selectedIdx]?.title ?? ''}
              placeholder="Card title"
              onChange={v => setCardField('title', v)} />
          </SplitRow>

          <LabelRow label="Card description" />
          <SplitRow currentText={origCard?.paragraph ?? ''} multiline>
            <DraftTextArea
              key={`contact-desc-${selectedIdx}`}
              initialValue={cardEdits[selectedIdx]?.paragraph ?? ''}
              placeholder="Card description" rows={3}
              onChange={v => setCardField('paragraph', v)} />
          </SplitRow>

          <LabelRow label="Card button" />
          <SplitRow currentText={origCard?.buttonText ?? ''}>
            <DraftTextField
              key={`contact-btn-${selectedIdx}`}
              initialValue={cardEdits[selectedIdx]?.buttonText ?? ''}
              placeholder="Text button"
              onChange={v => setCardField('buttonText', v)} />
          </SplitRow>
        </>
      )}
    </>
  );
};

export default CompanyContactEditor;